export async function isPushNotificationSupported(): Promise<boolean> {
    return "serviceWorker" in navigator && "PushManager" in window;
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
    if (!("Notification" in window)) {
        throw new Error("This browser does not support notifications");
    }

    if (Notification.permission === "granted") {
        return "granted";
    }

    if (Notification.permission !== "denied") {
        return await Notification.requestPermission();
    }

    return "denied";
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    const registration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
    });

    return registration;
}

export async function subscribeToPushNotifications(): Promise<PushSubscription | null> {
    const isSupported = await isPushNotificationSupported();
    if (!isSupported) {
        throw new Error("Push notifications are not supported");
    }

    const permission = await requestNotificationPermission();
    if (permission !== "granted") {
        throw new Error("Notification permission denied");
    }

    const registration = await registerServiceWorker();

    const response = await fetch("/notifications/push/public-key/");
    const data = await response.json();
    const vapidPublicKey = data.vapidPublicKey;

    const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });

    // Send subscription to backend
    await sendSubscriptionToBackend(subscription);

    return subscription;
}

export async function unsubscribeFromPushNotifications(): Promise<void> {
    const isSupported = await isPushNotificationSupported();
    if (!isSupported) {
        throw new Error("Push notifications are not supported");
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
        await sendUnsubscribeToBackend(subscription);
        await subscription.unsubscribe();
    }
}

export async function getPushSubscription(): Promise<PushSubscription | null> {
    const isSupported = await isPushNotificationSupported();
    if (!isSupported) {
        return null;
    }

    try {
        const registration = await navigator.serviceWorker.ready;
        return await registration.pushManager.getSubscription();
    } catch {
        return null;
    }
}

async function sendSubscriptionToBackend(
    subscription: PushSubscription,
): Promise<void> {
    const response = await fetch("/notifications/push/subscribe/", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCookie("csrftoken") || "",
        },
        body: JSON.stringify(subscription),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save subscription");
    }
}

async function sendUnsubscribeToBackend(
    subscription: PushSubscription,
): Promise<void> {
    const response = await fetch("/notifications/push/unsubscribe/", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCookie("csrftoken") || "",
        },
        body: JSON.stringify({
            endpoint: subscription.endpoint,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to unsubscribe");
    }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
        .replace(/\-/g, "+")
        .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

function getCookie(name: string): string | null {
    let cookieValue: string | null = null;
    if (document.cookie && document.cookie !== "") {
        const cookies = document.cookie.split(";");
        for (let cookie of cookies) {
            cookie = cookie.trim();
            if (cookie.substring(0, name.length + 1) === name + "=") {
                cookieValue = decodeURIComponent(
                    cookie.substring(name.length + 1),
                );
                break;
            }
        }
    }
    return cookieValue;
}
